import CloudLayer from './CloudLayer.js';
import SkyPresets from './SkyPresets.js';

/**
 * CloudBackdrop — Animated parallax cloud system.
 * Creates a canvas behind the scene and renders layered, evolving clouds
 * with camera-linked parallax for depth illusion.
 */

const RESOLUTION_SCALE = 0.5; // Render at half viewport resolution

export default class CloudBackdrop {
  constructor(containerEl, camera) {
    this._container = containerEl;
    this._camera = camera;
    this._canvas = null;
    this._ctx = null;
    this._layers = [];
    this._enabled = true;
    this._preset = null;
    this._originalBackground = null;
    this._layerCount = 3;
    this._tileSize = 32;
    this._frameCounter = 0;

    this._cachedGradient = null;
    this._cachedGradientHeight = 0;
    this._cachedGradientColors = null;

    this._createCanvas();
    this._resizeHandler = () => this.resize();
    window.addEventListener('resize', this._resizeHandler);
  }

  _createCanvas() {
    this._canvas = document.createElement('canvas');
    this._canvas.style.cssText =
      'position:absolute;top:0;left:0;width:100%;height:100%;' +
      'pointer-events:none;z-index:0;image-rendering:auto;';
    this._container.insertBefore(this._canvas, this._container.firstChild);
    this._ctx = this._canvas.getContext('2d');
    this.resize();
  }

  /**
   * Switch cloud atmosphere. Pass a preset name ('overworld', 'dungeon')
   * or a custom config object.
   */
  setPreset(nameOrConfig) {
    const config = typeof nameOrConfig === 'string' ? SkyPresets[nameOrConfig] : nameOrConfig;
    if (!config) return;

    this._preset = config;
    this._cachedGradient = null;

    // Build layers respecting current layer count and tile size settings
    this._rebuildLayers();

    // Make container background transparent so canvas shows through
    if (this._originalBackground === null) {
      this._originalBackground = this._container.style.background || '';
    }
    this._container.style.background = 'transparent';
  }

  setEnabled(enabled) {
    this._enabled = enabled;
    this._canvas.style.display = enabled ? '' : 'none';
    if (!enabled && this._originalBackground !== null) {
      this._container.style.background = this._originalBackground;
    } else if (enabled) {
      this._container.style.background = 'transparent';
    }
  }

  /**
   * Change the number of active cloud layers (1-3).
   * Keeps existing layers, just slices the array.
   */
  setLayerCount(count) {
    this._layerCount = Math.max(1, Math.min(3, count));
    if (this._preset) this._rebuildLayers();
  }

  getLayerCount() {
    return this._layerCount;
  }

  /**
   * Change the noise tile resolution (32, 48, or 64).
   * Lower = faster on mobile, higher = more detail.
   */
  setTileSize(size) {
    this._tileSize = size;
    if (this._preset) this._rebuildLayers();
  }

  getTileSize() {
    return this._tileSize;
  }

  _rebuildLayers() {
    this._frameCounter = 0;
    const config = this._preset;
    const count = this._layerCount;
    const tileSize = this._tileSize;
    // Take last N layers (front layers are most visible, keep those)
    const layerConfigs = config.layers.slice(config.layers.length - count);
    this._layers = layerConfigs.map((opts, i) => {
      const seed = (config.layers.length - count + i) * 1000 + 42;
      return new CloudLayer(opts, seed, tileSize);
    });
  }

  resize() {
    const w = Math.ceil(window.innerWidth * RESOLUTION_SCALE);
    const h = Math.ceil(window.innerHeight * RESOLUTION_SCALE);
    if (this._canvas.width !== w || this._canvas.height !== h) {
      this._canvas.width = w;
      this._canvas.height = h;
    }
  }

  /**
   * Called each frame from the game loop.
   */
  update(deltaTime) {
    if (!this._enabled || !this._preset) return;

    const layerCount = this._layers.length;

    // All layers accumulate time every frame (smooth drift/parallax)
    for (const layer of this._layers) {
      layer.advanceTime(deltaTime);
    }

    // Only one layer regenerates noise per frame (round-robin)
    if (layerCount > 0) {
      this._layers[this._frameCounter % layerCount].regenerateTile();
      this._frameCounter++;
    }

    this._renderFrame();
  }

  _renderFrame() {
    const ctx = this._ctx;
    const w = this._canvas.width;
    const h = this._canvas.height;

    // Draw sky gradient background (cached until preset or height changes)
    const colors = this._preset.skyGradient;
    if (!this._cachedGradient || this._cachedGradientHeight !== h || this._cachedGradientColors !== colors) {
      const grad = ctx.createLinearGradient(0, 0, 0, h);
      grad.addColorStop(0, colors[0]);
      grad.addColorStop(0.5, colors[1]);
      grad.addColorStop(1, colors[2]);
      this._cachedGradient = grad;
      this._cachedGradientHeight = h;
      this._cachedGradientColors = colors;
    }
    ctx.fillStyle = this._cachedGradient;
    ctx.fillRect(0, 0, w, h);

    // Render each cloud layer (back to front)
    for (const layer of this._layers) {
      layer.render(ctx, w, h, this._camera);
    }
  }

  destroy() {
    window.removeEventListener('resize', this._resizeHandler);
    if (this._canvas && this._canvas.parentNode) {
      this._canvas.parentNode.removeChild(this._canvas);
    }
    if (this._originalBackground !== null) {
      this._container.style.background = this._originalBackground;
    }
    this._layers = [];
    this._canvas = null;
    this._ctx = null;
  }
}
